import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useSociete } from "../contexts/SocieteContext";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Loader2, ArrowLeft, ArrowRightLeft, Send, MessageSquare, Phone, FileText, Bot, ClipboardList, ExternalLink, FileDown, Search, Languages, ListTodo, ChevronRight, Sparkles } from "lucide-react";
import EntityHero from "../components/entity-detail/EntityHero";
import EntityKpiBar from "../components/entity-detail/EntityKpiBar";
import PartnerBadge from "../components/affiliate/PartnerBadge";
import IntelAgentModal from "../components/intel/IntelAgentModal";
import AIProfileCard from "../components/entity-detail/AIProfileCard";
import EntityActivityTimeline from "../components/entity-detail/EntityActivityTimeline";
import SimilarProspectsCard from "../components/entity-detail/SimilarProspectsCard";
import { toast } from "sonner";
import QuestionnaireAnalysisCard from "../components/entity-detail/QuestionnaireAnalysisCard";
import QuestionnaireResponsesList from "../components/entity-detail/QuestionnaireResponsesList";
import ScoreSparkline from "../components/entity-detail/ScoreSparkline";
import EmailDraftCard from "../components/entity-detail/EmailDraftCard";
import CallTranscriptionPanel from "../components/entity-detail/CallTranscriptionPanel";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import AircallCallButton from "../components/AircallCallButton";

const STATUS_BADGE = {
  nouveau: { label: "Nouveau", color: "#3b82f6", bg: "bg-blue-500/15 text-blue-300" },
  contacte: { label: "Contacté", color: "#8b5cf6", bg: "bg-purple-500/15 text-purple-300" },
  qualifie: { label: "Qualifié", color: "#f7c935", bg: "bg-amber-500/15 text-amber-300" },
  proposition: { label: "Proposition", color: "#f97316", bg: "bg-orange-500/15 text-orange-300" },
  converti: { label: "Converti", color: "#22c55e", bg: "bg-emerald-500/15 text-emerald-300" },
  perdu: { label: "Perdu", color: "#ef4444", bg: "bg-rose-500/15 text-rose-300" },
};

const QUICK_STATUSES = ["nouveau", "contacte", "qualifie", "proposition", "perdu"];

export default function ProspectDetailPage() {
  const { prospectId } = useParams();
  const navigate = useNavigate();
  const { axiosAuth } = useAuth();
  const { societeApi } = useSociete();
  const api = societeApi();

  const [overview, setOverview] = useState(null);
  const [qstDetail, setQstDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [noteContent, setNoteContent] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [analyzingQst, setAnalyzingQst] = useState(false);
  const [showQstDialog, setShowQstDialog] = useState(false);
  const [showIntel, setShowIntel] = useState(false);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, qst] = await Promise.all([
        api.get(`/crm/prospects/${prospectId}/overview`),
        api.get(`/crm/prospects/${prospectId}/questionnaire-detail`).catch(() => ({ data: { has_questionnaire: false } })),
      ]);
      setOverview(ov.data);
      setQstDetail(qst.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Impossible de charger la fiche");
    } finally {
      setLoading(false);
    }
  }, [prospectId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const evaluate = async () => {
    setEvaluating(true);
    try {
      const r = await api.post(`/crm/prospects/${prospectId}/ai-evaluate`);
      setOverview((prev) => (prev ? { ...prev, ai_evaluation: r.data } : prev));
      // Recharge timeline pour faire apparaître l'event "ai_evaluation"
      reload();
    } catch (e) {
      alert(e.response?.data?.detail || "Échec de l'évaluation IA");
    } finally {
      setEvaluating(false);
    }
  };

  const analyzeQuestionnaire = async () => {
    setAnalyzingQst(true);
    try {
      const r = await api.post(`/crm/prospects/${prospectId}/analyze-questionnaire`);
      setQstDetail((prev) => (prev ? { ...prev, analysis: r.data } : prev));
      reload();
    } catch (e) {
      alert(e.response?.data?.detail || "Échec de l'analyse du questionnaire");
    } finally {
      setAnalyzingQst(false);
    }
  };

  const changeStatus = async (newStatus) => {
    try {
      await api.post(`/prospects/${prospectId}/status`, { status: newStatus });
      reload();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur changement de statut");
    }
  };

  const convertToCrm = async () => {
    if (!window.confirm("Convertir ce prospect en opportunité CRM ?")) return;
    try {
      const r = await api.post(`/prospects/${prospectId}/convert`);
      alert(`Opportunité créée : ${r.data.title || r.data.opportunity_id}`);
      navigate(`/admin/opportunite/${r.data.opportunity_id}`);
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur de conversion");
    }
  };

  const addNote = async () => {
    if (!noteContent.trim()) return;
    setSavingNote(true);
    try {
      await api.post(`/crm/prospects/${prospectId}/notes`, { content: noteContent });
      setNoteContent("");
      reload();
    } finally {
      setSavingNote(false);
    }
  };

  if (loading && !overview) {
    return (
      <div className="p-6 max-w-[1400px] mx-auto" data-testid="prospect-detail-loading">
        <div className="flex items-center gap-3 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" /> Chargement de la fiche…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-[1400px] mx-auto" data-testid="prospect-detail-error">
        <Link to="/admin/prospects" className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-1 mb-4">
          <ArrowLeft className="w-4 h-4" /> Retour à la liste
        </Link>
        <div className="bg-rose-500/10 text-rose-300 border border-rose-500/30 rounded-xl p-4 text-sm">{error}</div>
      </div>
    );
  }

  if (!overview) return null;

  const p = overview.prospect;
  const statusBadge = STATUS_BADGE[p.status] || STATUS_BADGE.nouveau;
  const calls = overview.calls || [];
  const invoices = overview.invoices || [];
  const timeline = overview.timeline || [];

  const exportPdf = async () => {
    try {
      const r = await api.get(`/crm/prospects/${prospectId}/export-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `fiche_${p.first_name || ""}_${p.last_name || prospectId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("Erreur export PDF");
    }
  };

  const heroActions = (
    <>
      {p.phone && <AircallCallButton api={api} prospect={p} onCalled={reload} />}
      {QUICK_STATUSES.map((st) => (
        <button
          key={st}
          onClick={() => changeStatus(st)}
          disabled={p.status === st}
          className={`text-[11px] px-2.5 py-1 rounded-lg transition-colors ${p.status === st ? "bg-white/15 text-white" : "bg-white/5 text-slate-400 hover:text-white border border-white/5 hover:border-white/15"}`}
          data-testid={`hero-status-${st}`}
        >
          {STATUS_BADGE[st]?.label || st}
        </button>
      ))}
      {p.status !== "converti" && (
        <button
          onClick={convertToCrm}
          className="text-[11px] px-2.5 py-1 rounded-lg bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 border border-emerald-500/20 inline-flex items-center gap-1"
          data-testid="hero-convert-btn"
        >
          <ArrowRightLeft className="w-3 h-3" /> Convertir en CRM
        </button>
      )}
      <button
        onClick={evaluate}
        disabled={evaluating}
        className="text-[11px] px-2.5 py-1 rounded-lg bg-[#f7c935]/15 text-[#f7c935] hover:bg-[#f7c935]/25 border border-[#f7c935]/30 inline-flex items-center gap-1 disabled:opacity-50"
        data-testid="hero-evaluate-btn"
      >
        {evaluating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bot className="w-3 h-3" />}
        Évaluer IA
      </button>
      <button
        onClick={exportPdf}
        className="text-[11px] px-2.5 py-1 rounded-lg bg-rose-500/15 text-rose-300 hover:bg-rose-500/25 border border-rose-500/30 inline-flex items-center gap-1"
        data-testid="hero-export-pdf-btn"
        title="Télécharger la fiche en PDF"
      >
        <FileDown className="w-3 h-3" /> Exporter PDF
      </button>
      <button
        onClick={() => setShowIntel(true)}
        className="text-[11px] px-2.5 py-1 rounded-lg bg-cyan-500/15 text-cyan-300 hover:bg-cyan-500/25 border border-cyan-500/30 inline-flex items-center gap-1"
        data-testid="hero-intel-btn"
        title="Lancer une enquête IA (Tavily + Pappers + Claude)"
      >
        <Search className="w-3 h-3" /> Enquête IA
      </button>
      <button
        onClick={async () => {
          try {
            const r = await axiosAuth().post("/aircall/translator/sessions/from-prospect", {
              prospect_id: prospectId,
              source_lang: "fr",
              target_lang: "en",
            });
            toast.success(`Session traduction lancée pour ${r.data.contact_name}`);
            navigate("/admin/commercial-panel");
          } catch (e) {
            toast.error(e.response?.data?.detail || "Impossible de lancer la session");
          }
        }}
        className="text-[11px] px-2.5 py-1 rounded-lg bg-purple-500/15 text-purple-300 hover:bg-purple-500/25 border border-purple-500/30 inline-flex items-center gap-1"
        data-testid="hero-translator-btn"
        title="Démarrer une session de traduction FR ↔ EN avec ce contact, et revenir sur votre panel commercial"
      >
        <Languages className="w-3 h-3" /> Traduire l'appel
      </button>
    </>
  );

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto space-y-5" data-testid="prospect-detail-page">
      {/* Back link */}
      <div className="flex items-center justify-between">
        <Link
          to="/admin/prospects"
          className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-1.5 transition-colors"
          data-testid="back-to-list-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Retour à la liste
        </Link>
      </div>

      {/* Hero */}
      <EntityHero
        entity={p}
        kind="prospect"
        statusBadge={statusBadge}
        aiEvaluation={overview.ai_evaluation}
        actions={heroActions}
      />

      {/* KPIs */}
      <EntityKpiBar kpis={overview.kpis} kind="prospect" />

      {/* Iter 233 — Attribution partenaire (affiliation long terme) */}
      {p?.partner_attribution?.partner_id && (
        <PartnerBadge attribution={p.partner_attribution} />
      )}

      {/* Main content tabs */}
      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="bg-[#0b0811] border border-white/5 w-full justify-start flex-wrap h-auto gap-0.5 p-1">
          <TabsTrigger value="overview" className="data-[state=active]:bg-[#3b82f6] data-[state=active]:text-white text-xs">
            Aperçu
          </TabsTrigger>
          <TabsTrigger value="ai" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c] text-xs" data-testid="tab-ai">
            <Bot className="w-3 h-3 mr-1" /> Profil IA
          </TabsTrigger>
          {qstDetail?.has_questionnaire && (
            <TabsTrigger value="questionnaire" className="data-[state=active]:bg-violet-500 data-[state=active]:text-white text-xs" data-testid="tab-questionnaire">
              <ClipboardList className="w-3 h-3 mr-1" /> Questionnaire
              {qstDetail?.analysis?.score !== undefined && (
                <span className="ml-1 text-[9px] font-bold px-1 py-0.5 rounded bg-white/15">
                  {qstDetail.analysis.score}/100
                </span>
              )}
            </TabsTrigger>
          )}
          <TabsTrigger value="timeline" className="data-[state=active]:bg-[#8b5cf6] data-[state=active]:text-white text-xs" data-testid="tab-timeline">
            Timeline ({timeline.length})
          </TabsTrigger>
          <TabsTrigger value="calls" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white text-xs" data-testid="tab-calls">
            <Phone className="w-3 h-3 mr-1" /> Appels ({calls.length})
          </TabsTrigger>
          <TabsTrigger value="docs" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-xs" data-testid="tab-docs">
            <FileText className="w-3 h-3 mr-1" /> Devis & Factures ({invoices.length})
          </TabsTrigger>
        </TabsList>

        {/* Overview tab — IA + dernière timeline + last call */}
        <TabsContent value="overview" className="space-y-5">
          <div className="grid lg:grid-cols-[1fr_auto] gap-3 items-stretch">
            <AIProfileCard evaluation={overview.ai_evaluation} onEvaluate={evaluate} loading={evaluating} compact />
            <div className="flex items-center justify-center">
              <ScoreSparkline api={api} prospectId={prospectId} />
            </div>
          </div>

          {/* Mini-résumé questionnaire cliquable → ouvre Dialog */}
          {qstDetail?.has_questionnaire && (
            <button
              onClick={() => setShowQstDialog(true)}
              className="w-full text-left bg-violet-500/5 hover:bg-violet-500/15 border border-violet-500/20 hover:border-violet-500/40 rounded-xl p-4 transition-all group"
              data-testid="qst-summary-trigger"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-violet-500/15 flex items-center justify-center flex-shrink-0">
                  <ClipboardList className="w-5 h-5 text-violet-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-white">Questionnaire de qualification</span>
                    {qstDetail.analysis ? (
                      <>
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase" style={{ background: qstDetail.analysis.qualification === 'hot' ? '#f43f5e22' : qstDetail.analysis.qualification === 'warm' ? '#f59e0b22' : '#0ea5e922', color: qstDetail.analysis.qualification === 'hot' ? '#fb7185' : qstDetail.analysis.qualification === 'warm' ? '#fbbf24' : '#38bdf8' }}>
                          {qstDetail.analysis.qualification === 'hot' ? 'CHAUD' : qstDetail.analysis.qualification === 'warm' ? 'TIÈDE' : 'FROID'} · {qstDetail.analysis.score}/100
                        </span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400">
                          {qstDetail.analysis.fields_answered}/{qstDetail.analysis.fields_visible} réponses
                        </span>
                      </>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 font-bold">
                        Analyse IA non générée
                      </span>
                    )}
                  </div>
                  {qstDetail.analysis?.verdict ? (
                    <p className="text-xs text-slate-300 mt-1.5 italic line-clamp-2">« {qstDetail.analysis.verdict} »</p>
                  ) : (
                    <p className="text-xs text-slate-500 mt-1.5">{qstDetail.questionnaire?.name}</p>
                  )}
                  {qstDetail.analysis?.recommended_action && (
                    <div className="text-[11px] text-amber-300 mt-2 flex items-center gap-1.5 truncate">
                      <span className="font-bold">→</span>
                      <span className="truncate">{qstDetail.analysis.recommended_action}</span>
                    </div>
                  )}
                </div>
                <ExternalLink className="w-4 h-4 text-slate-500 group-hover:text-white transition-colors flex-shrink-0 self-center" />
              </div>
            </button>
          )}

          {/* Quick add note */}
          <div className="bg-[#0b0811] rounded-xl border border-white/5 p-4">
            <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2 flex items-center gap-1.5">
              <MessageSquare className="w-3.5 h-3.5" /> Ajouter une note rapide
            </div>
            <Textarea
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              placeholder="Une observation, un compte rendu, un brief pour l'équipe…"
              rows={3}
              className="bg-[#14101c] border-white/10 text-white text-sm"
              data-testid="quick-note-input"
            />
            <div className="flex justify-end mt-2">
              <Button
                onClick={addNote}
                disabled={!noteContent.trim() || savingNote}
                size="sm"
                className="bg-[#3b82f6] text-white hover:bg-[#2563eb] text-xs"
                data-testid="quick-note-submit"
              >
                {savingNote ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />} Enregistrer
              </Button>
            </div>
          </div>

          {/* Last events preview */}
          <div>
            <h3 className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">Activité récente</h3>
            <EntityActivityTimeline timeline={timeline.slice(0, 5)} />
          </div>
        </TabsContent>

        {/* Full IA tab */}
        <TabsContent value="ai" className="space-y-4">
          <AIProfileCard evaluation={overview.ai_evaluation} onEvaluate={evaluate} loading={evaluating} />
          <ScoreSparkline api={api} prospectId={prospectId} />
          <EmailDraftCard api={api} prospectId={prospectId} prospectEmail={p.email} />
          <SimilarProspectsCard api={api} prospectId={prospectId} defaultMode="all" />
        </TabsContent>

        {/* Questionnaire tab — analyse IA + Q/A complet */}
        {qstDetail?.has_questionnaire && (
          <TabsContent value="questionnaire" className="space-y-4" data-testid="tab-questionnaire-content">
            <QuestionnaireAnalysisCard
              analysis={qstDetail.analysis}
              onAnalyze={analyzeQuestionnaire}
              loading={analyzingQst}
            />
            <QuestionnaireResponsesList
              visibleFields={qstDetail.visible_fields || []}
              responses={qstDetail.responses || {}}
              questionnaire={qstDetail.questionnaire}
            />
          </TabsContent>
        )}

        {/* Full timeline */}
        <TabsContent value="timeline">
          <div className="bg-[#0b0811] rounded-xl border border-white/5 p-4">
            <EntityActivityTimeline timeline={timeline} />
          </div>
        </TabsContent>

        {/* Calls tab — focus Aircall */}
        <TabsContent value="calls">
          {!calls.length ? (
            <div className="text-center py-10 text-sm text-slate-500">
              Aucun appel enregistré. Lancez un appel via Aircall depuis le bouton du Hero, ou importez vos appels via « Appels Aircall » sur la page Prospects.
            </div>
          ) : (
            <div className="space-y-3">
              {calls.map((c) => {
                const tierStyle = { hot: { label: "Chaud", cls: "bg-rose-500/15 text-rose-300" }, warm: { label: "Tiède", cls: "bg-amber-500/15 text-amber-300" }, cold: { label: "Froid", cls: "bg-sky-500/15 text-sky-300" } }[c.tier];
                return (
                <div
                  key={c.import_id || c.call_log_id || c.aircall_call_id}
                  className="bg-[#0b0811] rounded-xl border border-white/5 p-4 space-y-3"
                  data-testid="call-row"
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 text-xs text-slate-300">
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${c.direction === "inbound" ? "bg-emerald-500/15 text-emerald-300" : "bg-blue-500/15 text-blue-300"}`}
                      >
                        {c.direction === "inbound" ? "ENTRANT" : "SORTANT"}
                      </span>
                      {tierStyle && (
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${tierStyle.cls}`} data-testid="call-tier-badge">
                          {tierStyle.label} · {c.score}/100
                        </span>
                      )}
                      {c.simulate && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-[#f7c935]/10 text-[#f7c935] flex items-center gap-1">
                          <Sparkles className="w-2.5 h-2.5" /> Simulation
                        </span>
                      )}
                      <span>{c.duration ? `${c.duration}s` : "—"}</span>
                      {c.phone && <span className="text-slate-500">{c.phone}</span>}
                    </div>
                    <span className="text-[10px] text-slate-500">{c.created_at?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                  {c.ai_summary ? (
                    <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap" data-testid="call-ai-summary">
                      {c.ai_summary}
                    </p>
                  ) : (
                    <p className="text-xs text-slate-500 italic">Pas de résumé IA Aircall.</p>
                  )}
                  {(c.next_actions || []).length > 0 && (
                    <div className="space-y-1 pt-1 border-t border-white/5" data-testid="call-next-actions">
                      <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Prochaines actions IA</p>
                      {c.next_actions.map((a, i) => (
                        <p key={i} className="text-xs text-slate-400 flex items-start gap-1.5">
                          <ChevronRight className="w-3 h-3 mt-0.5 flex-shrink-0 text-[#00b388]" /> {a}
                        </p>
                      ))}
                    </div>
                  )}
                  {c.task_id && (
                    <p className="text-[10px] text-[#2ecfa5] flex items-center gap-1" data-testid="call-task-created">
                      <ListTodo className="w-3 h-3" /> Tâche de rappel créée dans Mes Tâches
                    </p>
                  )}
                  {/* Transcription IA Whisper + key moments */}
                  {c.call_log_id && (
                    <CallTranscriptionPanel
                      api={api}
                      callLogId={c.call_log_id}
                      recordingUrl={c.recording_url}
                    />
                  )}
                </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Devis & Factures */}
        <TabsContent value="docs">
          {!invoices.length ? (
            <div className="text-center py-10 text-sm text-slate-500">Aucun devis ni facture pour ce prospect.</div>
          ) : (
            <div className="space-y-2">
              {invoices.map((inv) => (
                <div
                  key={inv.invoice_id}
                  className="bg-[#0b0811] rounded-xl border border-white/5 p-3.5 flex items-center justify-between gap-3"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${inv.type === "facture" ? "bg-blue-500/15 text-blue-300" : inv.type === "avoir" ? "bg-rose-500/15 text-rose-300" : "bg-amber-500/15 text-amber-300"}`}
                      >
                        {inv.type}
                      </span>
                      <span className="text-sm font-semibold text-white">{inv.reference}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {inv.status} · émis le {(inv.issue_date || inv.created_at || "").slice(0, 10)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-white">{Number(inv.total_ttc || 0).toFixed(2)} €</div>
                    {inv.paid_amount > 0 && (
                      <div className="text-[11px] text-emerald-400">payé {Number(inv.paid_amount).toFixed(2)} €</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog questionnaire (déclenché depuis l'onglet Aperçu) */}
      <Dialog open={showQstDialog} onOpenChange={setShowQstDialog}>
        <DialogContent className="bg-[#14101c] border-violet-500/30 text-white max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="qst-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-violet-400" />
              Questionnaire & analyse IA
            </DialogTitle>
          </DialogHeader>
          {qstDetail?.has_questionnaire && (
            <div className="space-y-4 pt-2">
              <QuestionnaireAnalysisCard
                analysis={qstDetail.analysis}
                onAnalyze={analyzeQuestionnaire}
                loading={analyzingQst}
                compact
              />
              <QuestionnaireResponsesList
                visibleFields={qstDetail.visible_fields || []}
                responses={qstDetail.responses || {}}
                questionnaire={qstDetail.questionnaire}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Iter 235 — Modal Agent IA enquêteur */}
      <IntelAgentModal
        prospectId={p?.prospect_id}
        prospectName={`${p?.first_name || ""} ${p?.last_name || ""}`.trim() || p?.email || ""}
        open={showIntel}
        onClose={() => setShowIntel(false)}
      />
    </div>
  );
}
